import { useFormikContext } from 'formik';
import type { MutableRefObject } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import type { IntegrationSummary } from 'slab-core/src/types/api/IntegrationSummary/IntegrationSummary';

import { SlabContext } from '@core/context/SlabContext';
import type { QuoteFormikType } from '@core/formik/quote';
import { getFilteredProductsRefsByFormikProducts } from '@core/hooks/useProductSection';
import type { Company } from '@core/types/api/Company/Company';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import { hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

type IntegrationKey = string;

export const integrationKey = (integration: IntegrationSummary | null): IntegrationKey | null => {
  if (integration === null) return null;
  return integration.id ?? `type:${integration.integrationType}`;
};

const integrationLabel = (integration: IntegrationSummary | null): string => {
  if (integration === null) return 'dispatch system';

  return integration.integrationType;
};

/**
 * Gets the set of allowed integration keys from a company's dispatch customers.
 */
export const getAllowedIntegrationKeys = (company: Company | null): Set<IntegrationKey> => {
  if (!company || !hasValue(company.dispatchCustomers) || company.dispatchCustomers.length === 0) {
    return new Set<IntegrationKey>();
  }

  return new Set<IntegrationKey>(
    company.dispatchCustomers
      .map((dc) => integrationKey(dc.integrationSummary))
      .filter((key): key is IntegrationKey => key !== null),
  );
};

/**
 * Gets detailed mismatch information including missing integration keys and labels.
 */
export const getDispatchCustomerMismatchDetails = (
  products: Record<string, Product | null>,
  // integrations of company
  allowedIntegrationKeys: Set<IntegrationKey>,
): {
  missingLabels: string[];
  missingKeys: Set<IntegrationKey>;
  productsWithMismatch: Product[];
} => {
  const labels = new Set<string>();
  const keys = new Set<IntegrationKey>();
  const productsWithMismatch: Product[] = [];

  // if no integrations in the company, no need to check for mismatch
  if (allowedIntegrationKeys.size === 0) {
    return {
      missingLabels: [],
      missingKeys: new Set<IntegrationKey>(),
      productsWithMismatch: [],
    };
  }

  const productList = Object.values(products).filter(
    (product): product is Product => product !== null,
  );

  for (const product of productList) {
    const productIntegrationKey = integrationKey(product.integrationSummary ?? null);
    if (productIntegrationKey !== null && !allowedIntegrationKeys.has(productIntegrationKey)) {
      keys.add(productIntegrationKey);
      labels.add(integrationLabel(product.integrationSummary ?? null));
      productsWithMismatch.push(product);
    }
  }

  return {
    missingLabels: Array.from(labels),
    missingKeys: keys,
    productsWithMismatch,
  };
};

type UseDispatchCustomerMismatchProps = {
  curProductsRef: MutableRefObject<Record<string, Product>>;
};

type DispatchMismatch = {
  hasBlockingIssue: boolean;
  missingSystems: string[];
  removeUnlinkedProducts: () => void;
};

export const useDispatchCustomerMismatch = ({
  curProductsRef,
}: UseDispatchCustomerMismatchProps): DispatchMismatch => {
  const ctx = useContext(SlabContext);
  const formikBag = useFormikContext<QuoteFormikType>();
  const mainProductsByPlants = formikBag?.values.mainProductsByPlants ?? [];
  const additionalProductsByPlants = formikBag?.values.additionalProductsByPlants ?? [];

  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);
  const hasIntegrationExport = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagIntegrationExport,
  ]);
  const shouldCheck = usesDispatchCustomer && hasIntegrationExport;

  const companyId = formikBag?.values.company?.id ?? null;
  const { data: company } = useSlabQuery(
    'GET company by ID',
    { pathParams: { id: companyId ?? '' } },
    { enabled: shouldCheck && companyId !== null },
  );

  const allowedIntegrationKeys = useMemo(() => {
    return getAllowedIntegrationKeys(company ?? null);
  }, [company]);

  const { missingLabels, missingKeys } = useMemo(() => {
    if (!shouldCheck || companyId === null) {
      return { missingLabels: [] as string[], missingKeys: new Set<IntegrationKey>() };
    }

    const filteredProductsRefs = getFilteredProductsRefsByFormikProducts({
      curProductsRef: curProductsRef.current,
      formikProductsByPlants: [...mainProductsByPlants, ...additionalProductsByPlants],
    });

    return getDispatchCustomerMismatchDetails(filteredProductsRefs, allowedIntegrationKeys);
  }, [
    allowedIntegrationKeys,
    additionalProductsByPlants,
    companyId,
    curProductsRef,
    mainProductsByPlants,
    shouldCheck,
  ]);

  const removeUnlinkedProducts = useCallback(() => {
    if (!formikBag) return;
    if (missingKeys.size === 0) return;

    const filterProducts = (
      plants: QuoteFormikType['mainProductsByPlants'],
    ): QuoteFormikType['mainProductsByPlants'] =>
      plants.map((plant) => {
        const filtered = plant.products.filter((formikProduct) => {
          const productId = formikProduct.product.id;
          const product = hasValue(productId) ? curProductsRef.current[productId] : null;
          const key = integrationKey(product?.integrationSummary ?? null);
          return key === null || !missingKeys.has(key);
        });

        return { ...plant, products: filtered };
      });

    const nextMainProducts = filterProducts(mainProductsByPlants);
    const nextAdditionalProducts = filterProducts(additionalProductsByPlants);

    SetFormikValue(formikBag, 'mainProductsByPlants', nextMainProducts);
    SetFormikValue(formikBag, 'additionalProductsByPlants', nextAdditionalProducts);
  }, [additionalProductsByPlants, curProductsRef, formikBag, mainProductsByPlants, missingKeys]);

  const hasBlockingIssue = Boolean(formikBag) && shouldCheck && missingLabels.length > 0;

  return {
    hasBlockingIssue,
    missingSystems: missingLabels,
    removeUnlinkedProducts,
  };
};
